import { useRef, useCallback } from 'react';
import { ValidateHandle } from '../pages/DataSync/components/FormSections/ValidateHandle';
import { OptimizelyProduct, SyncStrategy } from '../types/DataSyncItem';
import { DataSyncViewModel } from '../types/DataSyncViewModel';

interface UseValidationResult {
  refs: {
    configRef: React.RefObject<ValidateHandle>;
    sourceRef: React.RefObject<ValidateHandle>;
    destinationRef: React.RefObject<ValidateHandle>;
    mappingsRef: React.RefObject<ValidateHandle>;
    scheduleRef: React.RefObject<ValidateHandle>;
  };
  isValid: () => boolean;
}

export function useValidation(
  viewModel: DataSyncViewModel,
  getStrategy: () => SyncStrategy
): UseValidationResult {
  const configRef = useRef<ValidateHandle>(null);
  const sourceRef = useRef<ValidateHandle>(null);
  const destinationRef = useRef<ValidateHandle>(null);
  const mappingsRef = useRef<ValidateHandle>(null);
  const scheduleRef = useRef<ValidateHandle>(null);

  const validateSchedule = useCallback(() => {
    const isScheduleValid = scheduleRef.current?.validate() ?? false;
    const strategy = getStrategy();
    const product = viewModel.source.product;

    return (
      strategy === SyncStrategy.WEBHOOK ||
      product === OptimizelyProduct.CGR ||
      product === OptimizelyProduct.HUB_APP ||
      isScheduleValid
    );
  }, [viewModel, getStrategy]);

  const isValid = useCallback(() => {
    const isConfigValid = configRef.current?.validate() ?? false;
    const isSourceValid = sourceRef.current?.validate() ?? false;
    const isDestinationValid = destinationRef.current?.validate() ?? false;
    const isMappingsValid = mappingsRef.current?.validate() ?? false;

    return (
      isConfigValid &&
      isSourceValid &&
      isDestinationValid &&
      isMappingsValid &&
      validateSchedule()
    );
  }, [validateSchedule]);

  return {
    refs: {
      configRef,
      sourceRef,
      destinationRef,
      mappingsRef,
      scheduleRef,
    },
    isValid,
  };
}
